import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Stack,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, useFieldArray } from 'react-hook-form';
import { toast } from 'react-toastify';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import { useUnitWeights } from '../hooks/useUnitWeights';
import { useStyles } from './styles';
import { ModelCostAdmin } from '@features/Admin/Billing/ui/ModelCostAdmin';

const UsageWeightsEditor: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const { showError } = useErrorHandler();

  const { formMethods, onSubmit, isLoading, isSaving, isDirty } = useUnitWeights();
  const { fields } = useFieldArray({ control: formMethods.control, name: 'weights' });

  const sortedFields = [...fields].sort((a, b) => a.sourceUnit.localeCompare(b.sourceUnit));

  const handleSave = async () => {
    try {
      await onSubmit();
      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('common.saved'),
          description: t('superAdminMainPage.companies.unitWeights.savedSuccess'),
        },
      });
    } catch (error) {
      showError({
        title: t('superAdminMainPage.companies.unitWeights.saveError'),
        error,
        suggestion: t('superAdminMainPage.companies.unitWeights.saveErrorSuggestion'),
      });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <Box className={classes.main}>
        <Box className={classes.header}>
          <Typography fontSize="18px" fontWeight={600}>
            {t('superAdminMainPage.companies.unitWeights.title')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaving || !isDirty}>
            {isSaving ? <CircularProgress size={20} color="inherit" /> : t('common.save')}
          </Button>
        </Box>

        <TableContainer className={classes.table}>
          <Table>
            <TableHead
              sx={{
                backgroundColor: theme.palette.extendedGrey?.lighter,
                height: '48px',
                borderTop: `1px solid ${theme.palette.divider}`,
              }}>
              <TableRow>
                <TableCell>
                  <Typography color={theme.palette.text.secondary} variant="h6">
                    {t('superAdminMainPage.companies.unitWeights.sourceUnit')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography color={theme.palette.text.secondary} variant="h6">
                    {t('superAdminMainPage.companies.unitWeights.limitUnit')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: 180 }}>
                  <Typography color={theme.palette.text.secondary} variant="h6">
                    {t('superAdminMainPage.companies.unitWeights.weight')}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedFields.map((field) => {
                const index = fields.findIndex((f) => f.id === field.id);
                const isDirtyField = !!formMethods.formState.dirtyFields?.weights?.[index]?.weight;

                return (
                  <TableRow
                    key={field.id}
                    hover
                    sx={{
                      backgroundColor: isDirtyField ? `${theme.palette.primary.main}10` : 'inherit',
                    }}>
                    <TableCell>
                      <Typography variant="subtitle1">{field.sourceUnit}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle1">{field.limitUnit}</Typography>
                    </TableCell>
                    <TableCell>
                      <Controller
                        control={formMethods.control}
                        name={`weights.${index}.weight`}
                        rules={{ min: 0 }}
                        render={({ field: inputField }) => (
                          <TextField
                            {...inputField}
                            type="number"
                            size="small"
                            onChange={(e) => {
                              const val = e.target.value;
                              inputField.onChange(val === '' ? 0 : Math.max(0, Number(val)));
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 140 }}
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </FormProvider>
  );
};

export const UnitWeightsPage: React.FC = () => {
  const [tab, setTab] = useState('prices');
  return (
    <Stack spacing={3}>
      <Typography variant="h4">Usage pricing</Typography>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-label="Usage administration">
        <Tab value="prices" label="Model costs" />
        <Tab value="weights" label="Usage limit weights" />
      </Tabs>
      {tab === 'prices' ? <ModelCostAdmin /> : <UsageWeightsEditor />}
    </Stack>
  );
};
